<template>
  <div :class="componentClass.root">
    <PmLoadingPure v-if="isLoading" size="default" />

    <template v-if="!isLoading">
      <div :class="componentClass.element('content')">
        <PmKeyValueListPure>
          <PmKeyValuePure v-if="IS_DEVELOPMENT" label="Id" variant="debug">
            {{ id }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Bezeichnung">
            {{ name }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Nummer">
            <PmPillPure :label="number" />
          </PmKeyValuePure>

          <PmKeyValuePure label="Typ">
            {{ type }}
          </PmKeyValuePure>

          <PmKeyValuePure label="Start/Ende">
            <template v-if="props.startDate && props.endDate">
              {{ startEndDateForText(props.startDate, props.endDate) }}
            </template>
          </PmKeyValuePure>

          <PmKeyValuePure label="Artikel">
            <PmPillWithLabelPure
              v-if="stockType"
              :pill="{ label: stockType.number }"
              :label="stockType.caption"
            />
          </PmKeyValuePure>

          <PmKeyValuePure label="Ressourcen">
            <PmRichTextPure v-if="resources?.length">
              <div
                v-for="resource in resources"
                :key="resource.id"
                class="PmDataModalStockTypeServicePure-resource"
              >
                <div class="PmDataModalStockTypeServicePure-resourceTitle">
                  <PmResourceShortPure
                    v-if="resource.abbreviation"
                    :interactive="false"
                    :label="resource.abbreviation"
                    :use-min-width="false"
                  />

                  <PmLinkPure
                    v-if="resource.title"
                    :label="resource.title"
                    @click="emit('openResource', resource.id)"
                  />

                  <template v-if="resource.startDate && resource.endDate">
                    {{
                      startEndDateForText(resource.startDate, resource.endDate)
                    }}
                  </template>
                </div>

                <ul>
                  <li
                    v-for="resourceAllocation in resource.resourceAllocations"
                    :key="resourceAllocation.id"
                  >
                    <div
                      class="PmDataModalStockTypeServicePure-resourceAllocation"
                    >
                      <PmLinkPure
                        :label="resourceAllocation.title"
                        @click="
                          emit('openResourceAllocation', resourceAllocation.id)
                        "
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </PmRichTextPure>
          </PmKeyValuePure>

          <PmKeyValuePure label="Erstellt">
            <template v-if="props.createdDate">
              {{ formatToRelativeDate(props.createdDate) }}
              <template v-if="props.createdBy">
                von {{ props.createdBy }}</template
              >
            </template>
          </PmKeyValuePure>

          <PmKeyValuePure label="Zuletzt geändert">
            <template v-if="props.editedDate">
              {{ formatToRelativeDate(props.editedDate) }}
              <template v-if="props.editedBy">
                von {{ props.editedBy }}</template
              >
            </template>
          </PmKeyValuePure>
        </PmKeyValueListPure>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useComponentClass } from '@thomasaull-shared/composables'

import { IS_DEVELOPMENT } from '@/constants/general'
import { formatToRelativeDate, startEndDateForText } from '@/utilities/date'

import PmKeyValueListPure from '@/components/basics/PmKeyValueList/PmKeyValueListPure.vue'
import PmKeyValuePure from '@/components/basics/PmKeyValueList/PmKeyValuePure.vue'
import PmPillPure from '@/components/basics/PmPillPure.vue'
import PmPillWithLabelPure from '@/components/basics/PmPillWithLabel/PmPillWithLabelPure.vue'
import PmLinkPure from '@/components/basics/PmLink/PmLinkPure.vue'
import PmLoadingPure from '@/components/basics/PmLoading/PmLoadingPure.vue'
import PmRichTextPure from '@/components/basics/PmRichText/PmRichTextPure.vue'
import PmResourceShortPure from '@/components/persoplan/PmResource/PmResourceShortPure.vue'

import type { Nilable } from '@/types/misc'

export interface Props {
  id: number
  isLoading?: boolean
  number?: Nilable<string>
  name?: Nilable<string>
  type?: Nilable<string>
  startDate?: Nilable<Date>
  endDate?: Nilable<Date>
  stockType?: Nilable<StockType>
  createdDate?: Nilable<Date>
  createdBy?: Nilable<string>
  editedDate?: Nilable<Date>
  editedBy?: Nilable<string>
  resources?: Resource[]
}

export type StockType = {
  id: number
  number: Nilable<string>
  caption: Nilable<string>
}

export type Resource = {
  id: number
  title: Nilable<string>
  abbreviation: Nilable<string>
  startDate: Nilable<Date>
  endDate: Nilable<Date>
  resourceAllocations: ResourceAllocation[]
}

export type ResourceAllocation = {
  id: number
  title: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  (event: 'openResource', id: number): void
  (event: 'openResourceAllocation', id: number): void
}>()

const componentClass = useComponentClass()
</script>

<style lang="scss">
.PmDataModalStockTypeServicePure {
  $block: &;

  &-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &-resource {
    margin-bottom: var(--space-gutters);
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &-resourceTitle {
    display: flex;
    gap: 4px;
  }

  &-resourceAllocation {
    display: flex;
    gap: 4px;
  }
}
</style>
